import React from 'react';
import { Tooltip } from 'antd';
import cn from 'classnames';

import {
  CircleEmptyIcon,
  CircleCheckIcon,
  SquareEmptyIcon,
  SquareCheckIcon,
  BetaIcon
} from '../../../icons';
import { Colors } from '../../../constants/colors.constants.js';
import colors from '../../../stylesheets/variables.scss';
import styles from './ToolbarSubmenu.module.scss';
import { getPermissionsByCommand } from '../../../utils/partialPermissionUtils';

export const TestIds = {
  SUBMENU: 'toolbar_submenu_root'
};

const IconBetaRender = (isBeta) => {
  if (!isBeta) {
    return;
  }

  return <BetaIcon color={colors.bluishGray30} />;
};

export const ToolbarSubmenu = (props) => {
  const {
    onCommandDispatched,
    t,
    className,
    config,
    subitemsCkeckedBackground,
    type,
    visualizationConfig = {}
  } = props;
  const { subitems, direction } = config;

  const {
    areLinksVisible,
    areSlackVisible,
    areNumtasksVisible,
    areBaselineVisible,
    areSubmittalsVisible,
    isTodaylineVisible
  } = visualizationConfig;

  const handleClick = (command, value) => {
    if (value) {
      command && onCommandDispatched(command, value);
    } else {
      command && onCommandDispatched(command);
    }
  };

  /**
   * This function renders the icon according to whether it is selected or not
   * @param {*} condition condition that needs to be rendered
   * @param {*} iconColorSelected color set in configuration
   * @returns component selection to render
   */
  const renderSelectedOrNot = (condition, iconColorSelected) => {
    return condition ? (
      <SquareCheckIcon color={iconColorSelected} />
    ) : (
      <SquareEmptyIcon color={Colors.GRAY_2} />
    );
  };

  /**
   * This function renders the subitems, according to the suitems setting
   * @param {*} command name of the function that is executed when selecting the item
   * @param {*} iconColorSelected icon color set in item configuration
   * @param {*} checked value that indicates if the item is selected or not
   * @returns component to render according to the configuration of the items
   */
  const renderItem = (command, iconColorSelected, checked) => {
    const iconColorSelectedDefault = Colors.DARK_MINT;
    const iconColor = iconColorSelected ?? iconColorSelectedDefault;
    if (command === 'TOGGLE_BASELINE_VISIBILITY') {
      return renderSelectedOrNot(areBaselineVisible, iconColor);
    }
    if (command === 'TOGGLE_TODAYLINE_VISIBILITY') {
      return renderSelectedOrNot(isTodaylineVisible, iconColor);
    }
    if (command === 'TOGGLE_SLACK_VISIBILITY') {
      return renderSelectedOrNot(areSlackVisible, iconColor);
    }
    if (command === 'TOGGLE_NUMTASKS_VISIBILITY') {
      return renderSelectedOrNot(areNumtasksVisible, iconColor);
    }
    if (command === 'TOGGLE_LINKS_VISIBILITY') {
      return renderSelectedOrNot(areLinksVisible, iconColor);
    }
    if (command === 'TOGGLE_SUBMITTALS_VISIBILITY') {
      return renderSelectedOrNot(areSubmittalsVisible, iconColor);
    }

    return checked ? (
      subitemsCkeckedBackground ? (
        <CircleCheckIcon color="#FFFFFF" />
      ) : (
        <CircleCheckIcon color={iconColor} />
      )
    ) : (
      <CircleEmptyIcon color={colors.gray30} />
    );
  };

  const cn_submenu_item = type
    ? styles['toolbar-massive-submenu__item']
    : styles['toolbar-submenu__item'];

  const filteredSubitems = subitems?.filter((item) => {
    return !item.hidden;
  });
  return (
    <ul
      className={cn(styles['toolbar-submenu'], className, {
        [styles['toolbar-submenu--right-direction']]: direction === 'RIGHT',
        [styles['toolbar-submenu--left-direction']]: direction === 'LEFT'
      })}
      data-testid={TestIds.SUBMENU}>
      {filteredSubitems?.map(
        (
          {
            icon: Icon,
            iconColor,
            isBeta,
            text,
            i18nKey,
            command,
            Comp,
            tooltip,
            tooltipI18nKey,
            subitems,
            checkable,
            checked,
            props: propsComp,
            iconColorSelected,
            value
          },
          i
        ) => {
          const hasPermission = getPermissionsByCommand(command);
          return (
            <li
              onClick={(e) => {
                if (hasPermission) {
                  handleClick(command, value);
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className={cn(cn_submenu_item, {
                [styles['toolbar-submenu__item--checked']]:
                  subitemsCkeckedBackground && checked
              })}
              key={i}
              style={{
                cursor: hasPermission ? 'pointer' : 'not-allowed'
              }}>
              {Comp && <Comp Icon={Icon} {...propsComp} />}
              {!Comp && (
                <Tooltip
                  title={hasPermission ? '' : t('not_permissions_actions')}
                  placement="top">
                  <span>
                    {checkable && (
                      <span
                        className={
                          styles['toolbar-submenu__item-check-circle']
                        }>
                        {renderItem(command, iconColorSelected, checked)}
                      </span>
                    )}
                    <span>
                      {Icon && <Icon color={iconColor} />}
                      <span className={styles['toolbar-submenu__item-text']}>
                        {text || t(i18nKey)}
                      </span>
                      {IconBetaRender(isBeta)}
                    </span>
                  </span>
                </Tooltip>
              )}
              {subitems && (
                <ToolbarSubmenu {...props} config={{ ...config, subitems }} />
              )}
            </li>
          );
        }
      )}
    </ul>
  );
};
